import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui';
import SecurityInfoModal from '../ui/SecurityInfoModal';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { trackFormInteraction, trackWorkflowStep, PRIVACY_CLASSES } from '../../utils/clarity';
import { formatPhoneNumberOnly, PHONE_MAX_LENGTH, PHONE_PLACEHOLDER, COUNTRY_CODES, parsePhoneNumber } from '../../utils/phoneFormat';
import withHamburgerMenu from '../hoc/withHamburgerMenu';

const Frame133: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress, completeTask } = useSessionPersistence();
  // Get user data from localStorage
  const accountToken = localStorage.getItem('account-token');
  let accountData = null;
  
  try {
    if (accountToken && typeof accountToken === 'string' && accountToken.split('.').length === 3) {
      const parts = accountToken.split('.');
      const base64Url = parts[1];
      if (base64Url) {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        accountData = JSON.parse(jsonPayload);
      }
    }
  } catch (error) {
    console.error('[Frame133] Failed to decode token:', error);
    // Fallback to session data
    accountData = null;
  }
  
  // Debug: log user data sources
  console.log('[Frame133] User data sources:', {
    accountToken,
    accountData,
    sessionName: session.name,
    sessionEmail: session.email
  });
  
  const firstName = accountData?.name?.split(' ')[0] || session.name?.split(' ')[0] || 'Your';
  const fullName = accountData?.name || session.name || '';
  const email = accountData?.email || session.email || '';

  // Debug: log derived user data
  console.log('[Frame133] Derived user data:', {
    firstName,
    fullName,
    email
  });

  const [birthdate, setBirthdate] = useState('');
  const [ssn, setSsn] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [showSecurityModal, setShowSecurityModal] = useState(false);

  const formatDate = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as MM/DD/YYYY
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // If the value is the masked version, clear it so user can re-enter
    if (value === maskBirthdate(birthdate)) {
      setBirthdate('');
      setBirthdateDisplay('');
      return;
    }

    const formatted = formatDate(value);
    setBirthdate(formatted);
    setBirthdateDisplay(formatted);

    // Save to session if valid
    if (isValidDate(formatted)) {
      const existingAnswers = (session.taskProgress?.about?.answers || {}) as Record<string, string>;
      updateTaskProgress('about', {
        birthdate: formatted
      }).then(() => {
        // Check if we have meaningful data to mark task as complete
        const answers: { birthdate?: string; ssn?: string } = { ...existingAnswers, birthdate: formatted };
        if (answers.birthdate && answers.ssn) {
          completeTask('about');
          console.log('[Frame133] Task marked complete - basic info filled');
        }
      }).catch(error => {
        console.error('[Frame133] Failed to save birthdate:', error);
      });
    }
  };

  const handleBirthdateFocus = () => {
    setIsBirthdateFocused(true);
    // Show actual value when focused
    setBirthdateDisplay(birthdate);
  };

  const handleBirthdateBlur = () => {
    setIsBirthdateFocused(false);
    // Mask after blur if value exists
    if (birthdate && isValidDate(birthdate)) {
      setBirthdateDisplay(maskBirthdate(birthdate));
    }
  };

  const formatSSN = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as XXX-XX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 5) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
    }
  };

  const [ssnDisplay, setSsnDisplay] = useState('');
  const [ssnTimeout, setSsnTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isSSNFocused, setIsSSNFocused] = useState(false);
  const [birthdateDisplay, setBirthdateDisplay] = useState('');
  const [, setIsBirthdateFocused] = useState(false);

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // If the value is the masked version, clear it so user can re-enter
    if (value === '•••-••-••••') {
      setSsn('');
      setSsnDisplay('');
      return;
    }
    
    const formatted = formatSSN(value);
    setSsn(formatted);
    setSsnDisplay(formatted);

    // Clear existing timeout
    if (ssnTimeout) {
      clearTimeout(ssnTimeout);
    }

    // Set new timeout to mask after 1 second (only if not focused)
    const timeout = setTimeout(() => {
      if (!isSSNFocused) {
        setSsnDisplay(maskSSN(formatted));
      }
    }, 1000);
    setSsnTimeout(timeout);

    // Save to session if valid
    if (isValidSSN(formatted)) {
      const existingAnswers = (session.taskProgress?.about?.answers || {}) as Record<string, string>;
      updateTaskProgress('about', {
        ssn: formatted
      }).then(() => {
        // Check if we have meaningful data to mark task as complete
        const answers: { birthdate?: string; ssn?: string } = { ...existingAnswers, ssn: formatted };
        if (answers.birthdate && answers.ssn) {
          completeTask('about');
          console.log('[Frame133] Task marked complete - basic info filled');
        }
      }).catch(error => {
        console.error('[Frame133] Failed to save SSN:', error);
      });
    }
  };

  const handleSSNFocus = () => {
    setIsSSNFocused(true);
    // Don't show the actual value - keep it masked for security
    if (ssnTimeout) {
      clearTimeout(ssnTimeout);
    }
  };

  const handleSSNBlur = () => {
    setIsSSNFocused(false);
    // Set timeout to mask after 1 second when blurring
    const timeout = setTimeout(() => {
      setSsnDisplay(maskSSN(ssn));
    }, 1000);
    setSsnTimeout(timeout);
  };

  const isValidDate = (value: string) => {
    // Simple MM/DD/YYYY validation
    const regex = /^(0[1-9]|1[0-2])\s*\/\s*(0[1-9]|[12][0-9]|3[01])\s*\/\s*(19|20)\d{2}$/;
    return regex.test(value.trim());
  };

  const isValidSSN = (value: string) => {
    // Accept "000-00-0000" or 9 digits
    const digits = value.replace(/\D/g, '');
    return digits.length === 9;
  };


  // Function to mask SSN except last 4 digits
  const maskSSN = (fullSSN: string) => {
    const digits = fullSSN.replace(/\D/g, '');
    if (digits.length !== 9) return '•••-••-••••';
    return `•••-••-${digits.slice(-4)}`;
  };

  // Function to mask birthdate showing only year
  const maskBirthdate = (fullDate: string) => {
    if (!fullDate || fullDate.length < 10) return '••/••/••••';
    const parts = fullDate.split('/');
    if (parts.length === 3) {
      return `••/••/${parts[2]}`;
    }
    return '••/••/••••';
  };

  // Phone formatting is now handled by shared utility in utils/phoneFormat.ts

  // Track if initial load has happened to prevent re-loading on every session change
  const initialLoadDone = useRef(false);

  // Load saved values ONCE when session data becomes available
  useEffect(() => {
    // Skip if we've already loaded initial data
    if (initialLoadDone.current) {
      return;
    }

    // Check if session has loaded from storage (has an accountId or any task progress)
    const sessionLoaded = session.accountId || Object.keys(session.taskProgress).length > 0;
    if (!sessionLoaded) {
      // Session still loading from storage, wait for it
      return;
    }

    // Session is loaded - mark as done and load data
    initialLoadDone.current = true;

    trackWorkflowStep('basic_information_form', 'started');
    trackFormInteraction('basic_info', 'focus');

    // Get session data
    const answers = (session?.taskProgress?.about?.answers || {}) as Record<string, string>;

    console.log('[Frame133] Loading data from session:', { 
      phone: session.phone, 
      answersPhone: answers.phone,
      birthdate: answers.birthdate 
    });

    // Load saved values from session
    if (answers.birthdate) {
      setBirthdate(answers.birthdate);
      setBirthdateDisplay(maskBirthdate(answers.birthdate)); // Display masked version
    }
    if (answers.ssn) {
      setSsn(answers.ssn); // Store full SSN in state
      setSsnDisplay(maskSSN(answers.ssn)); // Display masked version
    }

    // Phone: Load from taskProgress (where we save on blur), fallback to session.phone
    const phoneValue = answers.phone || session.phone;
    if (phoneValue) {
      const parsed = parsePhoneNumber(phoneValue);
      setCountryCode(parsed.countryCode);
      setPhoneNumber(parsed.phoneNumber);
      console.log('[Frame133] Loaded phone:', phoneValue, 'parsed:', parsed);
    }

    if (answers.street) {
      setStreet(answers.street);
    }
    if (answers.city) {
      setCity(answers.city);
    }
    if (answers.state) {
      setState(answers.state);
    }
    if (answers.zip) {
      setZip(answers.zip);
    }
  }, [session.accountId, session.taskProgress, session.phone]); // Re-check when session loads

  // Cleanup SSN timeout on unmount
  useEffect(() => {
    return () => {
      if (ssnTimeout) {
        clearTimeout(ssnTimeout);
      }
    };
  }, [ssnTimeout]);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title="Let's start with some basic information about you."
        />

        {/* User summary pill (name & email) */}
        <div className="mb-6 w-full max-w-sm h-12 bg-slate-100 border border-slate-400 rounded-[40px] flex items-center relative">
        <div className="w-10 h-10 bg-white rounded-full ml-1 flex-shrink-0 flex items-center justify-center">
          {/* Placeholder icon */}
          <svg className="w-4 h-5 text-cyan-700" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 0a6 6 0 016 6v1a6 6 0 11-12 0V6a6 6 0 016-6zm-8 18a8 8 0 0116 0v2H2v-2z" />
          </svg>
        </div>
        <div className="ml-4 flex-1 min-w-0 pr-12">
          <p className="font-sans text-lg font-semibold text-stone-800 leading-none truncate">{fullName}</p>
          <p className="font-sans text-base text-cyan-700 leading-none truncate">{email}</p>
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <svg className="w-4 h-4 text-cyan-700" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 0l3.09 6.26L20 7.27l-5 4.87L16.18 20 10 16.54 3.82 20 5 12.14 0 7.27l6.91-1.01L10 0z" />
          </svg>
        </div>
      </div>

        {/* Form fields */}
        <div className="mt-6 w-full max-w-md flex flex-col gap-8">
        {/* Birthdate */}
        <div className="flex flex-col gap-2">
          <label className="block font-sans text-lg font-medium text-stone-800 ml-3">Your Birthdate</label>
          <input
            type="text"
            placeholder="MM / DD / YYYY"
            value={birthdateDisplay || birthdate}
            onChange={(e) => {
              handleDateChange(e);
              trackFormInteraction('birthdate_input', 'blur');
            }}
            onFocus={() => {
              handleBirthdateFocus();
              trackFormInteraction('birthdate_input', 'focus');
            }}
            onBlur={handleBirthdateBlur}
            maxLength={10}
            className={`w-full h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.PERSONAL_INFO}`}
          />
        </div>
        {/* SSN */}
        <div className="flex flex-col gap-2">
          <label className="block font-sans text-lg font-medium text-stone-800 ml-3">Your Social Security Number</label>
          <input
            type="text"
            placeholder="000-00-0000"
            value={ssnDisplay}
            onChange={(e) => {
              handleSSNChange(e);
              trackFormInteraction('ssn_input', 'blur');
            }}
            onFocus={() => {
              handleSSNFocus();
              trackFormInteraction('ssn_input', 'focus');
            }}
            onBlur={handleSSNBlur}
            maxLength={11}
            className={`w-full max-w-xs h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.SENSITIVE_DATA}`}
          />
          <p className="mt-1 text-xs text-stone-500 italic ml-3">
            Optional — may be helpful for your executor later.
          </p>
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-2">
          <label className="block font-sans text-lg font-medium text-stone-800 ml-3">Your Phone Number</label>
          <div className="flex gap-2">
            {/* Country Code Dropdown */}
            <select
              value={countryCode}
              onChange={(e) => {
                setCountryCode(e.target.value);
                trackFormInteraction('country_code_select', 'change');
              }}
              className="w-24 h-9 bg-white border border-neutral-200 rounded px-2 font-sans text-base text-stone-800"
            >
              {COUNTRY_CODES.map((cc) => (
                <option key={cc.code} value={cc.code}>
                  {cc.code}
                </option>
              ))}
            </select>

            {/* Phone Number Input */}
            <input
              type="tel"
              placeholder={PHONE_PLACEHOLDER}
              value={formatPhoneNumberOnly(phoneNumber)}
              onChange={(e) => {
                // Extract only digits from user input - take up to 10 digits for US numbers
                const allDigits = e.target.value.replace(/\D/g, '');
                const digits = allDigits.slice(0, 10); // Limit to 10 digits for US phone

                console.log('[Frame133] Phone onChange:', {
                  inputValue: e.target.value,
                  extractedDigits: allDigits,
                  storedDigits: digits
                });

                // Store raw digits in state ONLY (don't save yet - save on blur)
                setPhoneNumber(digits);
                trackFormInteraction('phone_input', 'change');
              }}
              onBlur={() => {
                // Save phone when user leaves the field (like SettingsPage save button)
                const fullPhone = phoneNumber ? `${countryCode}${phoneNumber}` : '';
                console.log('[Frame133] Phone onBlur - saving:', fullPhone);
                
                // Save to taskProgress (consistent with other Frame133 fields)
                updateTaskProgress('about', {
                  phone: fullPhone
                });
                trackFormInteraction('phone_input', 'blur');
              }}
              onFocus={() => trackFormInteraction('phone_input', 'focus')}
              maxLength={PHONE_MAX_LENGTH + 1}
              className={`flex-1 h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.PERSONAL_INFO}`}
            />
          </div>
        </div>

        {/* Street Address */}
        <div className="flex flex-col gap-2">
          <label className="block font-sans text-lg font-medium text-stone-800 ml-3">Street Address</label>
          <input
            type="text"
            placeholder="123 Main Street"
            value={street}
            onChange={(e) => {
              setStreet(e.target.value);
              updateTaskProgress('about', {
                street: e.target.value
              });
              trackFormInteraction('street_input', 'blur');
            }}
            onFocus={() => trackFormInteraction('street_input', 'focus')}
            className={`w-full h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.PERSONAL_INFO}`}
          />
        </div>

        {/* City, State, Zip */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-2 flex-1">
            <label className="block font-sans text-lg font-medium text-stone-800 ml-3">City</label>
            <input
              type="text"
              placeholder="City"
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                updateTaskProgress('about', {
                  city: e.target.value
                });
                trackFormInteraction('city_input', 'blur');
              }}
              onFocus={() => trackFormInteraction('city_input', 'focus')}
              className={`w-full h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.PERSONAL_INFO}`}
            />
          </div>
          <div className="flex flex-col gap-2" style={{width: '120px'}}>
            <label className="block font-sans text-lg font-medium text-stone-800 ml-3">State</label>
            <input
              type="text"
              placeholder="CA"
              value={state}
              onChange={(e) => {
                const val = e.target.value.toUpperCase().slice(0, 2);
                setState(val);
                updateTaskProgress('about', {
                  state: val
                });
                trackFormInteraction('state_input', 'blur');
              }}
              onFocus={() => trackFormInteraction('state_input', 'focus')}
              maxLength={2}
              className={`w-full h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.PERSONAL_INFO}`}
            />
          </div>
          <div className="flex flex-col gap-2" style={{width: '140px'}}>
            <label className="block font-sans text-lg font-medium text-stone-800 ml-3">Zip Code</label>
            <input
              type="text"
              placeholder="12345"
              value={zip}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                setZip(val);
                updateTaskProgress('about', {
                  zip: val
                });
                trackFormInteraction('zip_input', 'blur');
              }}
              onFocus={() => trackFormInteraction('zip_input', 'focus')}
              maxLength={5}
              className={`w-full h-9 bg-white border border-neutral-200 rounded px-3 font-sans text-base text-stone-800 placeholder-stone-500 ${PRIVACY_CLASSES.PERSONAL_INFO}`}
            />
          </div>
        </div>
      </div>

      {/* Security banner */}
      <div className="flex items-center bg-orange-100 rounded w-full max-w-[653px] min-h-[40px] py-2 px-3 mt-14 mx-auto md:mx-0 md:ml-0 lg:ml-[253px]">
        <div className="w-3.5 h-3.5 flex-shrink-0 bg-orange-400 mr-2" />
        <p className="text-orange-700 text-sm font-medium font-sans">
          End of an Era uses best-in-class security to keep your information private. <span 
            className="underline cursor-pointer hover:text-orange-800" 
            onClick={() => setShowSecurityModal(true)}
          >
            Learn More.
          </span>
        </p>
      </div>

        {/* Validation messages */}
        <div className="mt-4 text-red-700 text-sm font-sans">
          {!isValidDate(birthdate) && birthdate && <p>Please enter a valid date (MM / DD / YYYY).</p>}
          {!isValidSSN(ssn) && ssn && <p>Please enter a 9-digit SSN.</p>}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Setup Your Plan"
        subtitle="Add your basic personal information"
        onNext={() => navigate('/frame-137')}
        onBack={() => navigate('/frame-132')}
        nextText="Next"
        backText="Back"
        nextDisabled={false}
        progressPercent={25}
      />


      {/* Security Information Modal */}
      <SecurityInfoModal 
        isOpen={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
      />
    </ResponsiveContainer>
  );
};

export default withHamburgerMenu(Frame133);